import { CONTENT_MAX_WIDTH, centeredContent } from '@/components/layout/content-width';
import { renderKeyboardAwareScroll } from '@/components/layout/keyboard-aware-screen-scroll';
import { TAB_BOTTOM_SPACING } from '@/components/layout/tab-screen-wrapper';
import { ActivityCard } from '@/features/activity/components/activity-card';
import { useAppTheme } from '@/providers/theme-provider';
import type { FeedItem } from '@/types/feed';
import React, { useCallback } from 'react';
import { ActivityIndicator, FlatList, Platform, RefreshControl, View } from 'react-native';

// Virtualization tuning modeled on social-app's PostFeed. Feed cards are tall
// and image-heavy, so keep a modest render window and small batches.
const FEED_WINDOW_SIZE = 9;
const FEED_INITIAL_NUM_TO_RENDER = 5;
const FEED_UPDATE_CELLS_BATCHING_PERIOD_MS = 40;
const FEED_MAX_TO_RENDER_PER_BATCH_IOS = 5;
const FEED_MAX_TO_RENDER_PER_BATCH_DEFAULT = 1;

interface FeedListProps {
  items: FeedItem[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isRefetching: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  onLike?: (checkInId: string, currentlyLiked: boolean) => void;
  ListEmptyComponent?: React.ReactElement;
}

export function FeedList({
  items,
  hasNextPage,
  isFetchingNextPage,
  isRefetching,
  onLoadMore,
  onRefresh,
  onLike,
  ListEmptyComponent,
}: FeedListProps) {
  const theme = useAppTheme();

  const renderItem = useCallback(
    ({ item }: { item: FeedItem }) => (
      <ActivityCard item={item} onLike={onLike} />
    ),
    [onLike],
  );

  const keyExtractor = useCallback(
    (item: FeedItem) => item.checkIn.id,
    [],
  );

  const ListFooterComponent = isFetchingNextPage ? (
    <View className="py-4 items-center">
      <ActivityIndicator color={theme.colors.primary} />
    </View>
  ) : null;

  return (
    <FlatList
      testID="feed-list"
      data={items}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      onEndReached={onLoadMore}
      onEndReachedThreshold={0.5}
      windowSize={FEED_WINDOW_SIZE}
      initialNumToRender={FEED_INITIAL_NUM_TO_RENDER}
      maxToRenderPerBatch={
        Platform.OS === 'ios'
          ? FEED_MAX_TO_RENDER_PER_BATCH_IOS
          : FEED_MAX_TO_RENDER_PER_BATCH_DEFAULT
      }
      updateCellsBatchingPeriod={FEED_UPDATE_CELLS_BATCHING_PERIOD_MS}
      // Clipping offscreen subviews is a native optimization; on web it can
      // blank cells during fast scroll.
      removeClippedSubviews={Platform.OS !== 'web'}
      ListFooterComponent={ListFooterComponent}
      ListEmptyComponent={ListEmptyComponent}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      renderScrollComponent={renderKeyboardAwareScroll}
      refreshControl={
        <RefreshControl
          refreshing={isRefetching && !isFetchingNextPage}
          onRefresh={onRefresh}
          tintColor={theme.colors.primary}
        />
      }
      contentContainerStyle={{
        paddingBottom: TAB_BOTTOM_SPACING,
        paddingHorizontal: 16,
        paddingTop: 16,
        ...centeredContent(CONTENT_MAX_WIDTH),
      }}
    />
  );
}
